import { Link } from "react-router";
import { listCollection } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import PageHero from "../components/PageHero/PageHero.jsx";
import FeatureList from "../components/FeatureList/FeatureList.jsx";
import SplitFeature from "../components/SplitFeature/SplitFeature.jsx";
import EntryCard from "../components/EntryCard/EntryCard.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import styles from "./bim.module.css";

const PAGE = {
  title: "The 3D Visualization platform",
  description:
    "View, navigate and share an interactive 3D model of your infrastructure project using nothing more than a web browser.",
  path: "/123bim",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "3D Visualization", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/123bim/overview",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

export async function loader() {
  const related = listCollection("projects")
    .filter((e) => e.product !== "rsa3d")
    .slice(0, 3);
  return { related };
}

const COMPONENTS = [
  {
    title: "3D Visualization",
    href: "/123bim",
    text: "Cloud platform: stakeholders open the current design in a browser, on any device, with nothing to install.",
  },
  {
    title: "Virtual Tour",
    href: "/123bim/virtual-tour",
    text: "Guided tours for public involvement: the public follows a route through the design at their own pace.",
  },
  {
    title: "Navigator",
    href: "/123bim/navigator",
    text: "Desktop application for offline work, animations and HDTV image and video export.",
  },
];

const FEATURES = [
  {
    title: "Remote access",
    text: "Open the 3D model from any computer with complete interactive viewing and navigation.",
  },
  {
    title: "Sync browsing",
    text: "Share a live session with team members and key stakeholders and look at the same view together.",
  },
  {
    title: "Always up to date",
    text: "Everyone reviews the current cloud model, so the conference room never argues about versions.",
  },
  {
    title: "Communication",
    text: "Complex concepts explained to stakeholders without high-end software or skills on their side.",
  },
  {
    title: "Collaboration",
    text: "An immersive, real-time living document that people explore, analyze and experience rather than merely view.",
  },
  {
    title: "On-demand assets",
    text: "Images, virtual tours and videos, including 360, produced from the same model whenever you need them.",
  },
];

export default function Bim({ loaderData }) {
  return (
    <>
      <PageHero
        eyebrow="3D Visualization"
        title="3D Visualization is collaboration technology"
        lead="View, navigate and share your interactive 3D model using nothing more than a web browser. Engineers' 3D documents are huge and need dedicated software; 3D Visualization makes them a link."
        image="/images/site/123bim/overview"
        imageAlt="A 3D Visualization model of a highway interchange open in a web browser"
        breadcrumbs={CRUMBS}
      />

      <section className="section" aria-labelledby="components-title">
        <div className="container">
          <SectionHeader
            id="components-title"
            eyebrow="Three ways to use it"
            title="One model, three doors into it"
          />
          <div className="grid grid-3">
            {COMPONENTS.map((c) => (
              <article key={c.href} className={styles.card}>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
                <Link to={c.href}>
                  Learn more<span className="sr-only"> about {c.title}</span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt" aria-labelledby="how-title">
        <div className="container">
          <SplitFeature
            id="how-title"
            eyebrow="How it works"
            title="From CAD to a shareable model"
            image="/images/site/123bim/browser"
            imageAlt="3D Visualization model controls in a browser window"
          >
            <p>
              Complex project designs created in any industry-standard CAD platform are converted
              into 3D digital models and published to the cloud.
            </p>
            <p>
              Departments of transportation and engineering firms across the U.S. use it for civil
              infrastructure projects of every size that need frequent public engagement.
            </p>
            <p>
              <strong>Publish, don&apos;t render:</strong> the model is the deliverable, and every
              image, tour and video comes from it on demand.
            </p>
          </SplitFeature>
        </div>
      </section>

      <section className="section" aria-labelledby="features-title">
        <div className="container">
          <SectionHeader
            id="features-title"
            eyebrow="Features"
            title="Built for reviews, hearings and public meetings"
          />
          <FeatureList items={FEATURES} />
        </div>
      </section>

      {loaderData.related.length ? (
        <section className="section section-alt" aria-labelledby="projects-title">
          <div className="container">
            <SectionHeader
              id="projects-title"
              eyebrow="Projects"
              title="3D Visualization at work"
            />
            <div className="grid grid-3">
              {loaderData.related.map((p) => (
                <EntryCard
                  key={p.slug}
                  kind="project"
                  href={`/projects/${p.slug}`}
                  image={p.image}
                  imageAlt={p.imageAlt}
                  title={p.title}
                  description={p.description}
                  meta={p.client}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CTABand
        title="See your design in 3D"
        text="Send a DGN, DWG or Civil 3D file and get a link to your own interactive model."
      />
    </>
  );
}
